// @ts-check
/** @typedef {import('../types/api').RecoverySummary} RecoverySummary */
/** @typedef {import('../types/api').Product} Product */
/** @typedef {import('../types/api').Order} Order */
/** @typedef {import('../types/api').Customer} Customer */
/** @typedef {import('../types/api').Vendor} Vendor */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const recoveryService = {
  /**
   * @returns {Promise<RecoverySummary>}
   */
  async getRecoverySummary() {
    const data = await api.get('/api/admin/recovery/summary');
    // Backend returns {deleted_products, deleted_orders, deleted_customers,
    // deleted_vendors, deleted_expenses, total_deleted_records} — the old keys
    // ('products', 'orders', ...) never existed on the response, so this threw
    // on every valid call.
    assertShape(
      data,
      ['deleted_products', 'deleted_orders', 'deleted_customers', 'deleted_vendors'],
      'recoveryService.getRecoverySummary',
    );
    return data;
  },

  // Products

  /**
   * @returns {Promise<Product[]>}
   */
  async getDeletedProducts() {
    const data = await api.get('/api/admin/recovery/products');
    assertArray(data, 'recoveryService.getDeletedProducts');
    return data;
  },

  /**
   * @param {string} productId
   * @returns {Promise<Object>}
   */
  async restoreProduct(productId) {
    return api.post(`/api/admin/recovery/products/${productId}`);
  },

  /**
   * @param {string} productId
   * @returns {Promise<void>}
   */
  async permanentlyDeleteProduct(productId) {
    return api.del(`/api/admin/recovery/products/${productId}`);
  },

  // Orders

  /**
   * @returns {Promise<Order[]>}
   */
  async getDeletedOrders() {
    const data = await api.get('/api/admin/recovery/orders');
    assertArray(data, 'recoveryService.getDeletedOrders');
    return data;
  },

  /**
   * @param {string} orderId
   * @returns {Promise<Object>}
   */
  async restoreOrder(orderId) {
    return api.post(`/api/admin/recovery/orders/${orderId}`);
  },

  /**
   * @param {string} orderId
   * @returns {Promise<void>}
   */
  async permanentlyDeleteOrder(orderId) {
    return api.del(`/api/admin/recovery/orders/${orderId}`);
  },

  // Customers

  /**
   * @returns {Promise<Customer[]>}
   */
  async getDeletedCustomers() {
    const data = await api.get('/api/admin/recovery/customers');
    assertArray(data, 'recoveryService.getDeletedCustomers');
    return data;
  },

  /**
   * @param {string} customerId
   * @returns {Promise<Object>}
   */
  async restoreCustomer(customerId) {
    return api.post(`/api/admin/recovery/customers/${customerId}`);
  },

  /**
   * @param {string} customerId
   * @returns {Promise<void>}
   */
  async permanentlyDeleteCustomer(customerId) {
    return api.del(`/api/admin/recovery/customers/${customerId}`);
  },

  // Vendors

  /**
   * @returns {Promise<Vendor[]>}
   */
  async getDeletedVendors() {
    const data = await api.get('/api/admin/recovery/vendors');
    assertArray(data, 'recoveryService.getDeletedVendors');
    return data;
  },

  /**
   * @param {string} vendorId
   * @returns {Promise<Object>}
   */
  async restoreVendor(vendorId) {
    return api.post(`/api/admin/recovery/vendors/${vendorId}`);
  },

  /**
   * @param {string} vendorId
   * @returns {Promise<void>}
   */
  async permanentlyDeleteVendor(vendorId) {
    return api.del(`/api/admin/recovery/vendors/${vendorId}`);
  },
};

export default recoveryService;
